/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * CART-14 — Shipping-level promotional treatment in the Cart Summary.
 *
 * Renders one brand-red row per entry in `shippingItems[0].priceAdjustments[]`
 * directly below the Shipping row in OrderSummary.ServicesAndFees. Two display shapes:
 *
 *   - Free-shipping: adjustment zeroes out shippingTotal → "Free Shipping" label
 *   - Amount-off:    partial discount                    → "[calloutMsg] (-$X.XX)"
 *
 * The display label prefers `calloutMsg` (the BM-authored marketing message) and
 * falls back to `itemText` (the internal promo name) when `calloutMsg` is absent.
 * Both are rendered as plain text — no dangerouslySetInnerHTML.
 * Visual token (`text-promo-discount`) matches CART-12 and CART-13 for parity.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/currency';
import { useOrderSummaryContext } from './context';

export interface ShippingPromoLineProps {
    adjustments: Array<{ priceAdjustmentId?: string; itemText?: string; calloutMsg?: string; price?: number }>;
    shippingTotal: number | null | undefined;
}

export function ShippingPromoLine({ adjustments, shippingTotal }: ShippingPromoLineProps): ReactElement | null {
    const { t, i18n } = useTranslation('cart');
    const { currency } = useOrderSummaryContext();

    if (adjustments.length === 0) return null;

    return (
        <>
            {adjustments.map((adj) => {
                const price = adj.price;
                if (typeof price !== 'number') return null;

                const isFreeShipping = shippingTotal === 0;
                // Prefer the BM-authored callout message; fall back to internal itemText then generic badge.
                const label = isFreeShipping
                    ? (t as (key: string) => string)('cartSummary.shipping.freeShippingLabel')
                    : (adj.calloutMsg ??
                      adj.itemText ??
                      (t as (key: string) => string)('cartSummary.shipping.promoBadge'));

                return (
                    <div
                        key={adj.priceAdjustmentId}
                        data-testid="shipping-promo-line"
                        className="flex items-center justify-between">
                        <span className="text-promo-discount">{label}</span>
                        {!isFreeShipping && (
                            <span className="text-promo-discount">
                                {formatCurrency(price, i18n.language, currency)}
                            </span>
                        )}
                    </div>
                );
            })}
        </>
    );
}
